import config from '@/config';
import { createRouter, createWebHistory } from 'vue-router';
import ReservationView from '@/views/ReservationView.vue';
import ChoicesRouterView from '@/views/ChoicesRouter.vue';
import CustomerView from '@/views/CustomerView.vue';
import AfrondenView from '@/views/AfrondenView.vue';

const routesPaths = {
	'nl-NL': {
		arrangement_hotel_choices: 'jouw-arrangement-keuzes-hotel-rooms',
		arrangement_product_choices: 'jouw-arrangement-keuzes-product',
		choices: 'jouw-keuzes',
		customer: 'gegevens',
		lunch_and_diner: 'lunch-and-diner',
		hotel_rooms: 'hotel-rooms',
		treatments: 'behandelingen',
		rituals: 'rituelen',
	},
	'de-DE': {
		arrangement_hotel_choices: 'deine-arrangement-entscheidungen-hotelzimmer',
		arrangement_product_choices: 'deine-arrangement-entscheidungen',
		choices: 'deine-entscheidungen',
		customer: 'personendaten',
		lunch_and_diner: 'mittagessen-und-abendessen',
		hotel_rooms: 'hotelzimmer',
		treatments: 'behandlungen',
		rituals: 'rituale',
	},
	'en-US': {
		arrangement_hotel_choices: 'your-arrangement-choices-hotel-rooms',
		arrangement_product_choices: 'your-arrangement-choices',
		choices: 'your-choices',
		customer: 'customer-details',
		lunch_and_diner: 'lunch-and-diner',
		hotel_rooms: 'hotel-rooms',
		treatments: 'treatments',
		rituals: 'rituals',
	},
	'fr-FR': {
		arrangement_hotel_choices: 'vos-choix-d-arrangement-chambres-dhotel',
		arrangement_product_choices: 'vos-choix-d-arrangement',
		choices: 'vos-choix',
		customer: 'details-du-client',
		lunch_and_diner: 'dejeuner-et-diner',
		hotel_rooms: 'chambres-dhotel',
		treatments: 'traitements',
		rituals: 'rituels',
	},
} as const;

const keepQueryParameters = ['ga_origin', '_gl', 'ro'] as const;

const localeRoutePath = routesPaths[config.locale as keyof typeof routesPaths];

export const routes = [
	{
		path: '/',
		name: 'reservation',
		meta: { stepable: true, validator: 'reservation' },
		component: ReservationView,
	},
	{
		path: `/${localeRoutePath.choices}`,
		name: 'choices',
		redirect: { name: 'reservation' },
		component: ChoicesRouterView,
		children: [
			{
				path: localeRoutePath.arrangement_hotel_choices,
				name: 'arrangement_hotel_choices',
				meta: { stepable: true, validator: 'arrangement_hotel_choices' },
				component: () => import('@/views/ArrangementHotelChoicesView.vue'),
			},
			{
				path: localeRoutePath.arrangement_product_choices,
				name: 'arrangement_product_choices',
				meta: { stepable: true, validator: 'arrangement_product_choices' },
				component: () => import('@/views/ArrangementProductChoicesView.vue'),
			},
			{
				path: localeRoutePath.lunch_and_diner,
				name: 'lunch_and_diner',
				meta: { stepable: true, validator: 'upsell_horeca_and_hotel' },
				component: () => import('@/views/LunchAndDinerSelectView.vue'),
			},
			{
				path: localeRoutePath.treatments,
				name: 'treatments',
				meta: { stepable: true },
				component: () => import('@/views/TreatmentSelectView.vue'),
			},
			{
				path: localeRoutePath.rituals,
				name: 'rituals',
				meta: { stepable: true },
				component: () => import('@/views/RitualsSelectView.vue'),
			},
		],
	},
	{
		path: `/${localeRoutePath.customer}`,
		name: 'customer',
		meta: { stepable: true },
		component: CustomerView,
	},
	{
		path: '/afronden',
		name: 'complete',
		meta: { stepable: true },
		component: AfrondenView,
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'notfound',
		redirect: { name: 'reservation' },
	},
];

/**
 * Ugly way to remove unused choice steps dynamically
 */
export const removeOptionalRoutes = (routeNames: string[]) => {
	const removedOptionalRoutes: string[] = [];

	routeNames.forEach((routeName) => {
		if (router.hasRoute(routeName)) {
			router.removeRoute(routeName);
			removedOptionalRoutes.push(routeName);
		}
	});

	if (removedOptionalRoutes.length > 0) console.warn('Removed optional routes:', removedOptionalRoutes);

	const choicesRoute = routes.find((route) => route.name === 'choices');
	if (choicesRoute) {
		choicesRoute.children = choicesRoute.children?.filter((route) => !routeNames.includes(route.name));
	}
};

const router = createRouter({
	history: createWebHistory(config.baseUrl),
	routes,

	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		} else {
			return { top: 0 /*behavior: 'smooth'*/ };
		}
	},
});

router.beforeEach((to, from, next) => {
	const isToReadOnly = to.query.ro === 'true';
	const isFromReadOnly = from.query.ro === 'true';
	const readOnlyMode = isToReadOnly || isFromReadOnly;
	const sourceReadOnlyQuery = isToReadOnly ? to.query : isFromReadOnly ? from.query : {};
	const readOnlyQuery = {
		...(sourceReadOnlyQuery.lang ? { lang: sourceReadOnlyQuery.lang } : {}),
		...(sourceReadOnlyQuery.product ? { product: sourceReadOnlyQuery.product } : {}),
		ro: 'true',
	};

	if (readOnlyMode && to.name !== 'reservation' && to.name !== 'notfound') {
		next({ name: 'reservation', query: readOnlyQuery });
		return;
	}

	const queryParams = from.query;

	if (!Object.keys(to.query).length && queryParams !== null && Object.keys(queryParams).length) {
		const keep = Object.fromEntries(
			keepQueryParameters.map((col) => [col, queryParams[col]]).filter(([, value]) => !!value),
		);

		if (Object.keys(keep).length) {
			next({ name: to.name, query: keep });
		} else {
			next();
		}
	} else {
		next();
	}
});

export default router;
